import { useEffect, useRef, useState } from "react";
import { Menu, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import MobileMenu from "@/components/MobileMenu";
import { LOGO_URL } from "@/lib/ebook";

const EXPERIENCES = [
  { label: "Royal Essence", to: "/collection/royal-essence" },
  { label: "Sovereign Canvas", to: "/collection/sovereign" },
  { label: "Majestic Vibes", to: "/collection/majestic-vibes" },
  { label: "Free Photo Guide", to: "/ebook" },
];

const NAV = [
  { label: "Home", to: "/" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Black Excellence Unveiled", to: "/black-excellence-unveiled" },
  { label: "Our Story", to: "/#our-story" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || pathname !== "/"
            ? "bg-[#080808]/85 backdrop-blur-xl border-b border-white/5"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 py-2.5 md:py-3 flex items-center justify-between gap-4">
          <Link to="/" data-testid="logo-link" className="flex items-center flex-shrink-0 group">
            <img
              src={LOGO_URL}
              alt="Crowned Canvas Creations — official logo"
              className="h-14 md:h-[76px] w-auto object-contain transition-transform group-hover:scale-[1.03]"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            <Link
              to="/"
              data-testid="nav-home"
              className="text-[11px] uppercase tracking-[0.22em] text-white/70 hover:text-[#D4AF37] transition-colors"
            >
              Home
            </Link>

            {/* Experiences dropdown */}
            <div ref={dropRef} className="relative">
              <button
                type="button"
                onClick={() => setDropOpen((o) => !o)}
                data-testid="nav-experiences"
                aria-expanded={dropOpen}
                aria-haspopup="true"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-white/70 hover:text-[#D4AF37] transition-colors"
              >
                The Experiences
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${dropOpen ? "rotate-180" : ""}`} />
              </button>
              {dropOpen && (
                <div
                  data-testid="experiences-dropdown"
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-64 bg-[#111111] border border-white/10 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.9)] animate-in fade-in slide-in-from-top-2 duration-200"
                >
                  <div className="h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" aria-hidden />
                  {EXPERIENCES.map((e) => (
                    <Link
                      key={e.to}
                      to={e.to}
                      onClick={() => setDropOpen(false)}
                      data-testid={`nav-exp-${e.label.toLowerCase().replace(/\s/g, "-")}`}
                      className="block px-5 py-3.5 text-xs uppercase tracking-[0.18em] text-white/75 hover:bg-[#824937]/25 hover:text-[#D4AF37] transition-colors border-b border-white/5 last:border-b-0"
                    >
                      {e.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {NAV.slice(1).map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-[11px] uppercase tracking-[0.22em] text-white/70 hover:text-[#D4AF37] transition-colors"
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/#experiences"
              data-testid="header-cta"
              className="hidden md:inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.22em] bg-[#D4AF37] text-[#080808] px-4 md:px-5 py-2.5 md:py-3 hover:bg-[#e6c245] transition-all font-semibold"
            >
              Create Your Masterpiece
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              data-testid="mobile-menu-btn"
              className="lg:hidden w-10 h-10 flex items-center justify-center text-white/80 hover:text-[#D4AF37]"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}
